import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { faChevronDown, faChevronRight, faEnvelope, faMessage } from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MifSubscriptionAuthService } from '@shared/auth/mif-subscription.auth.service';
import { constants } from '@shared/constants/constants';
import { OtpFormControl } from '@shared/form/otp-form-control/otp-form-control';
import {
  AuthorizationDeliveryMode,
  ClientCodeRequest,
  ProspectCodeRequest,
  UserLoginInfo,
  ValidateCodeRequest
} from '@shared/webapi/MIF.Subscription.WebApi';
import { AUTH_TOO_MANY_REQUESTS_MESSAGE } from '../../../../shared/constants/auth-messages';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import {
  isInvalidCodeClientAuthError,
  isProspectCodeRequest,
  parseRequestCodeClientError
} from '../../../../store/client-store/client.helpers';
import {
  ClientAuthErrorCode,
  ClientAuthorizationStatus,
  ClientAuthRequest,
  RequestCodeClientAuthError,
  ValidateCodeClientAuthError
} from '../../../../store/client-store/client.models';

type UserLoginInfoWithUserId = UserLoginInfo & { userId?: string };
type ValidateCodeRequestWithUserId = ValidateCodeRequest & { userId?: string };

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-otp-check',
  templateUrl: './otp-check.component.html',
  styleUrls: ['./otp-check.component.scss']
})
export class OtpCheckComponent {
  private static readonly resendDelaySeconds = 30;

  @Input() prefilledUserId: string | null = null;
  @Input() prefilledSessionId: string | null = null;
  @Input() showCancel = true;
  @Input() centerSubmitButton = false;
  @Input() useAnonymousContactText = false;

  authorizationDeliveryMode: AuthorizationDeliveryMode | null = null;
  authRequest: ClientAuthRequest | null = null;
  authSessionId: string | null = null;
  authUserId: string | null = null;
  resumeSessionId: string | null = null;
  maskedPhone: string | null = null;
  maskedEmail: string | null = null;
  resendSecondsRemaining = 0;
  private resendTimerId: number | null = null;
  requestCodeAuthError: RequestCodeClientAuthError | null = null;
  private prefilledRequestCodeAuthError: RequestCodeClientAuthError | null = null;
  authError: ValidateCodeClientAuthError | null = null;
  clientAuthorizationStatus = ClientAuthorizationStatus.Idle;
  lockoutSecondsRemaining = 0;
  private lockoutTimerId: number | null = null;
  private isLockoutResolved = false;
  invalidCodeMessage: string | null = null;

  @Output() cancel = new EventEmitter<void>();

  readonly channel = OtpDeliveryChannel;
  readonly faChevronRight = faChevronRight;
  readonly faChevronDown = faChevronDown;
  readonly faMessage = faMessage;
  readonly faEnvelope = faEnvelope;
  readonly tooManyRequestsMessage = AUTH_TOO_MANY_REQUESTS_MESSAGE;

  readonly form = new FormGroup({
    code: new OtpFormControl(true, false, {
      hideErrorMessages: true
    })
  });
  readonly codeControl = this.form.get('code');

  isExpanded = false;
  selectedChannel = OtpDeliveryChannel.Sms;
  canRequestNewCode = false;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private clientFacade: ClientFacade,
    private mifSubscriptionAuthService: MifSubscriptionAuthService
  ) {
    this.syncResendTimer(false);
    this.initSubscriptions();
  }

  ngOnInit(): void {
    this.resumeSessionId = this.prefilledSessionId;

    if (this.prefilledUserId && !this.authRequest) {
      this.syncResendTimer(true);
    }
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
    this.clearLockoutTimer();
  }

  toggleExpanded(): void {
    if (!this.canRequestNewCode) {
      return;
    }

    this.isExpanded = !this.isExpanded;
  }

  selectChannel(channel: OtpDeliveryChannel): void {
    const authorizationDeliveryMode = this.getAuthorizationDeliveryMode(channel);

    this.selectedChannel = channel;
    this.authorizationDeliveryMode = authorizationDeliveryMode;
    this.clientFacade.setAuthorizationDeliveryMode(authorizationDeliveryMode);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onValidateOtpCode(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const code = this.codeControl?.value;
    const sessionId = this.effectiveSessionId;
    const userId = this.effectiveUserId;

    if (!code || (!sessionId && !userId)) {
      return;
    }

    const request = new ValidateCodeRequest({
      sessionId: sessionId || undefined,
      code
    }) as ValidateCodeRequestWithUserId;

    if (!sessionId && userId) {
      request.userId = userId;
    }

    this.clearInvalidCodeError();
    this.clientFacade.validateClientCode(request);
  }

  onRequestNewCode(channel: OtpDeliveryChannel): void {
    // avoid email otp fomr prospect until the server-side fallback flow is hardened
    if (channel === OtpDeliveryChannel.Email && this.isProspectAuthRequest) {
      return;
    }

    this.selectChannel(channel);
    const authorizationDeliveryMode = this.getAuthorizationDeliveryMode(channel);

    if (!this.canRequestNewCode) {
      return;
    }

    if (!this.authRequest && this.prefilledUserId) {
      this.requestCodeForPrefilledUserId(authorizationDeliveryMode);
      return;
    }

    if (!this.authRequest) {
      return;
    }

    this.startResendTimer();

    if (isProspectCodeRequest(this.authRequest)) {
      const request = new ProspectCodeRequest({
        ...this.authRequest,
        // phoneNumber: '+995511113389',
        deliveryMode: authorizationDeliveryMode
      });

      this.clientFacade.submitProspectAuthentication(request);
      return;
    }

    const request = new ClientCodeRequest({
      ...this.authRequest,
      deliveryMode: authorizationDeliveryMode
    });

    this.clientFacade.submitClientAuthentication(request);
  }

  private requestCodeForPrefilledUserId(deliveryMode: AuthorizationDeliveryMode = AuthorizationDeliveryMode.Sms): void {
    if (!this.prefilledUserId) {
      return;
    }

    this.startResendTimer();
    this.mifSubscriptionAuthService
      .resumeSessionByUserId(this.prefilledUserId, deliveryMode)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: authResponse => {
          this.prefilledRequestCodeAuthError = null;
          this.resumeSessionId = authResponse.sessionId || null;
          this.maskedPhone = authResponse.phoneNumber || null;
          this.maskedEmail = authResponse.email || null;

          if (!this.resumeSessionId) {
            this.resetResendRequestState();
            return;
          }

          this.cdr.markForCheck();
        },
        error: error => {
          this.prefilledRequestCodeAuthError = parseRequestCodeClientError(error);
          this.resetResendRequestState();
          this.cdr.markForCheck();
        }
      });
  }

  private initSubscriptions(): void {
    this.clientFacade.requestCodeAuthError$
      .pipe(untilDestroyed(this))
      .subscribe((authError: RequestCodeClientAuthError | null) => {
        this.requestCodeAuthError = authError;
        this.cdr.markForCheck();
      });

    this.codeControl?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.clearInvalidCodeError();
    });

    this.clientFacade.validateCodeAuthError$
      .pipe(untilDestroyed(this))
      .subscribe((authError: ValidateCodeClientAuthError | null) => {
        this.authError = authError;

        if (this.isInvalidCodeAuthError(authError)) {
          this.invalidCodeMessage = 'Le code saisi est invalide.';
        } else {
          this.clearInvalidCodeError();
        }

        if (authError?.errorCode === ClientAuthErrorCode.AccessBlocked) {
          this.isLockoutResolved = false;

          if (authError.lockoutEnd) {
            this.startLockoutTimer(authError.lockoutEnd);
          } else {
            this.clearLockoutTimer();
            this.lockoutSecondsRemaining = 0;
            this.cdr.markForCheck();
          }

          return;
        }

        this.isLockoutResolved = false;
        this.clearLockoutTimer();
        this.lockoutSecondsRemaining = 0;
        this.cdr.markForCheck();
      });

    this.clientFacade.authRequest$.pipe(untilDestroyed(this)).subscribe((authRequest: ClientAuthRequest | null) => {
      this.authRequest = authRequest;
      this.cdr.markForCheck();
    });

    this.clientFacade.authResponse$.pipe(untilDestroyed(this)).subscribe((authResponse: UserLoginInfo | null) => {
      this.authSessionId = authResponse?.sessionId || null;
      this.authUserId = this.getAuthUserId(authResponse);
      this.maskedPhone = authResponse?.phoneNumber || null;
      this.maskedEmail = authResponse?.email || null;

      if (authResponse) {
        this.syncResendTimer(true);
        return;
      }

      this.cdr.markForCheck();
    });

    this.clientFacade.authorizationDeliveryMode$
      .pipe(untilDestroyed(this))
      .subscribe((authorizationDeliveryMode: AuthorizationDeliveryMode | null) => {
        this.authorizationDeliveryMode = authorizationDeliveryMode;
        this.cdr.markForCheck();
      });

    this.clientFacade.clientAuthorizationStatus$
      .pipe(untilDestroyed(this))
      .subscribe((clientAuthorizationStatus: ClientAuthorizationStatus) => {
        this.clientAuthorizationStatus = clientAuthorizationStatus;
        this.cdr.markForCheck();
      });
  }

  private syncResendTimer(startIfMissing: boolean): void {
    const resendAvailableAt = this.getStoredResendAvailableAt();

    if (resendAvailableAt && resendAvailableAt > Date.now()) {
      this.startResendTimer(resendAvailableAt);
      return;
    }

    if (resendAvailableAt) {
      this.clearStoredResendAvailableAt();
    }

    if (startIfMissing) {
      this.startResendTimer();
      return;
    }

    this.updateResendState(0);
  }

  private startResendTimer(resendAvailableAt: number = Date.now() + OtpCheckComponent.resendDelaySeconds * 1000): void {
    this.clearResendTimer();
    this.isExpanded = false;
    localStorage.setItem(constants.localStorageKeys.clientAuthOtpResendAvailableAt, JSON.stringify(resendAvailableAt));

    this.updateResendState(this.getResendSecondsRemaining(resendAvailableAt));

    if (this.resendSecondsRemaining === 0) {
      this.clearStoredResendAvailableAt();
      return;
    }

    this.resendTimerId = window.setInterval(() => {
      const secondsRemaining = this.getResendSecondsRemaining(resendAvailableAt);

      this.updateResendState(secondsRemaining);

      if (secondsRemaining === 0) {
        this.clearStoredResendAvailableAt();
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimerId !== null) {
      window.clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  }

  private startLockoutTimer(lockoutEnd: string): void {
    const lockoutEndTime = new Date(lockoutEnd).getTime();

    if (Number.isNaN(lockoutEndTime)) {
      this.clearLockoutTimer();
      this.lockoutSecondsRemaining = 0;
      return;
    }

    this.clearLockoutTimer();
    this.updateLockoutState(this.getLockoutSecondsRemaining(lockoutEndTime));

    if (this.lockoutSecondsRemaining === 0) {
      return;
    }

    this.lockoutTimerId = window.setInterval(() => {
      const secondsRemaining = this.getLockoutSecondsRemaining(lockoutEndTime);

      this.updateLockoutState(secondsRemaining);

      if (secondsRemaining === 0) {
        this.isLockoutResolved = true;
        this.clearLockoutTimer();
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  private clearLockoutTimer(): void {
    if (this.lockoutTimerId !== null) {
      window.clearInterval(this.lockoutTimerId);
      this.lockoutTimerId = null;
    }
  }

  private clearStoredResendAvailableAt(): void {
    localStorage.removeItem(constants.localStorageKeys.clientAuthOtpResendAvailableAt);
  }

  private resetResendRequestState(): void {
    this.clearStoredResendAvailableAt();
    this.clearResendTimer();
    this.updateResendState(0);
  }

  private getStoredResendAvailableAt(): number | null {
    const storedValue = localStorage.getItem(constants.localStorageKeys.clientAuthOtpResendAvailableAt);

    if (!storedValue) {
      return null;
    }

    const resendAvailableAt = Number(JSON.parse(storedValue));

    return Number.isFinite(resendAvailableAt) ? resendAvailableAt : null;
  }

  private getResendSecondsRemaining(resendAvailableAt: number): number {
    return Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
  }

  private getLockoutSecondsRemaining(lockoutEndTime: number): number {
    return Math.max(0, Math.floor((lockoutEndTime - Date.now()) / 1000));
  }

  private updateResendState(secondsRemaining: number): void {
    this.resendSecondsRemaining = secondsRemaining;
    this.canRequestNewCode = secondsRemaining === 0;
    this.cdr.markForCheck();
  }

  private updateLockoutState(secondsRemaining: number): void {
    this.lockoutSecondsRemaining = secondsRemaining;
    this.cdr.markForCheck();
  }

  private isInvalidCodeAuthError(authError: ValidateCodeClientAuthError | null): boolean {
    return isInvalidCodeClientAuthError(authError);
  }

  private clearInvalidCodeError(): void {
    this.invalidCodeMessage = null;
    this.cdr.markForCheck();
  }

  private getSelectedChannel(): OtpDeliveryChannel {
    if (this.authorizationDeliveryMode === AuthorizationDeliveryMode.Email) {
      return OtpDeliveryChannel.Email;
    }

    return this.selectedChannel;
  }

  private getAuthorizationDeliveryMode(channel: OtpDeliveryChannel): AuthorizationDeliveryMode {
    if (channel === OtpDeliveryChannel.Email) {
      return AuthorizationDeliveryMode.Email;
    }

    return AuthorizationDeliveryMode.Sms;
  }

  private getProspectPhoneNumber(): string | null {
    if (!this.authRequest || !isProspectCodeRequest(this.authRequest)) {
      return null;
    }

    return this.authRequest.phoneNumber || null;
  }

  private getProspectEmail(): string | null {
    if (!this.authRequest || !isProspectCodeRequest(this.authRequest)) {
      return null;
    }

    return this.authRequest.email || null;
  }

  private getDisplayPhoneNumber(): string | null {
    return this.maskedPhone || this.getProspectPhoneNumber();
  }

  private getDisplayEmail(): string | null {
    return this.maskedEmail || this.getProspectEmail();
  }

  get verificationText(): string {
    if (this.getSelectedChannel() === OtpDeliveryChannel.Email) {
      if (this.shouldUseAnonymousContactText) {
        return 'Si une adresse e-mail vérifiée est associée à votre compte, un code vient de vous être envoyé.';
      }

      const email = this.getDisplayEmail() || 'votre adresse email';

      return `Vous avez reçu un code de vérification sur votre adresse email ${email}. Il est valide pendant 5 minutes. Saisissez-le ci-dessous :`;
    }

    if (this.shouldUseAnonymousContactText) {
      return 'Vous avez reçu un code de vérification sur votre téléphone mobile. Il est valide pendant 5 minutes. Saisissez-le ci-dessous :';
    }

    const phone = this.getDisplayPhoneNumber() || 'votre téléphone mobile';

    return `Vous avez reçu un code de vérification sur le numéro de mobile ${phone}. Il est valide pendant 5 minutes. Saisissez-le ci-dessous :`;
  }

  get smsText(): string {
    const phone = this.getDisplayPhoneNumber() || 'votre téléphone portable';

    return `Nous vous enverrons un SMS avec un code de vérification sur ${phone}.`;
  }

  get showEmailDeliveryOption(): boolean {
    return !this.isProspectAuthRequest;
  }

  get emailText(): string {
    if (this.shouldUseAnonymousContactText) {
      return 'Nous vous enverrons un e-mail avec un code de vérification si une adresse vérifiée est associée à votre compte.';
    }

    const email = this.getDisplayEmail() || 'votre adresse email';

    return `Nous vous enverrons un e-mail avec un code de vérification sur ${email}.`;
  }

  get resendCooldownText(): string {
    return `Si vous n'avez pas recu le code, vous pourrez en demander un nouveau dans ${this.resendSecondsRemaining} secondes.`;
  }

  get effectiveSessionId(): string | null {
    return this.authSessionId || this.resumeSessionId || this.prefilledSessionId;
  }

  get effectiveUserId(): string | null {
    return this.authUserId || this.prefilledUserId;
  }

  get canSubmitCode(): boolean {
    return !!this.effectiveSessionId || !!this.effectiveUserId;
  }

  get showResendActions(): boolean {
    return !!this.authRequest || !!this.prefilledUserId;
  }

  get showResendTimer(): boolean {
    return this.showResendActions || !!this.prefilledUserId;
  }

  get showInvalidCodeError(): boolean {
    return !!this.invalidCodeMessage;
  }

  get showTooManyRequestsScreen(): boolean {
    return this.effectiveRequestCodeAuthError?.errorCode === ClientAuthErrorCode.TooManyRequests;
  }

  get isAccessBlocked(): boolean {
    return (
      this.clientAuthorizationStatus === ClientAuthorizationStatus.Blocked &&
      this.authError?.errorCode === ClientAuthErrorCode.AccessBlocked &&
      !this.isLockoutResolved
    );
  }

  get showLockoutCountdown(): boolean {
    return this.lockoutSecondsRemaining > 0;
  }

  get lockoutRemainingText(): string {
    if (this.lockoutSecondsRemaining >= 60) {
      const minutesRemaining = Math.ceil(this.lockoutSecondsRemaining / 60);

      return `${minutesRemaining} minute${minutesRemaining > 1 ? 's' : ''}`;
    }

    return `${this.lockoutSecondsRemaining} seconde${this.lockoutSecondsRemaining > 1 ? 's' : ''}`;
  }

  private getAuthUserId(authResponse: UserLoginInfo | null): string | null {
    return (authResponse as UserLoginInfoWithUserId | null)?.userId || null;
  }

  private get isProspectAuthRequest(): boolean {
    return !!this.authRequest && isProspectCodeRequest(this.authRequest);
  }

  private get effectiveRequestCodeAuthError(): RequestCodeClientAuthError | null {
    return this.prefilledRequestCodeAuthError || this.requestCodeAuthError;
  }

  private get shouldUseAnonymousContactText(): boolean {
    return this.useAnonymousContactText || this.isPrefilledResumeSessionFlow;
  }

  private get isPrefilledResumeSessionFlow(): boolean {
    return !!this.prefilledUserId;
  }
}

export enum OtpDeliveryChannel {
  Sms = 'sms',
  Email = 'email'
}
